"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractMeasureFromBundle = exports.extractLibrariesFromBundle = exports.isValidLibraryURL = exports.extractMeasurementPeriod = exports.codeableConceptToPopulationType = exports.isEpisodeOfCareMeasure = void 0;
var Enums_1 = require("../types/Enums");
var CustomErrors_1 = require("../types/errors/CustomErrors");
/**
 * The extension that defines the population basis. This is used to determine if the measure is an episode of care or
 * patient based measure.
 */
var POPULATION_BASIS_EXT = 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-populationBasis';
/**
 * Check if a measure is an episode of care measure or not. Look for the cqfm-populationBasis extension.
 * If it is found return true if valueCode is not 'boolean' otherwise return false.
 *
 * @param {fhir4.Measure} measure FHIR Measure resource.
 * @returns {boolean} true if this is an episode of care, false if it is a patient measure.
 */
function isEpisodeOfCareMeasure(measure) {
    var _a;
    var popBasisExt = (_a = measure.extension) === null || _a === void 0 ? void 0 : _a.find(function (ext) { return ext.url == POPULATION_BASIS_EXT; });
    if (popBasisExt != undefined) {
        return popBasisExt.valueCode != 'boolean';
    }
    else {
        return false;
    }
}
exports.isEpisodeOfCareMeasure = isEpisodeOfCareMeasure;
/**
 * Population Type Code system.
 */
var POPULATION_TYPE_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/measure-population';
/**
 * Converts FHIR CodeableConcept value for the measure population type to a PopulationType enum value.
 *
 * @param {fhir4.CodeableConcept|undefined} concept The FHIR CodeableConcept value for the measure population.
 * @returns {PopulationType|null} null if not a proper population type. The PopulationType if it is.
 */
function codeableConceptToPopulationType(concept) {
    var _a;
    var populationTypeCoding = (_a = concept === null || concept === void 0 ? void 0 : concept.coding) === null || _a === void 0 ? void 0 : _a.find(function (coding) {
        return coding.system == POPULATION_TYPE_CODESYSTEM;
    });
    if ((populationTypeCoding === null || populationTypeCoding === void 0 ? void 0 : populationTypeCoding.code) != null && Object.values(Enums_1.PopulationType).includes(populationTypeCoding.code)) {
        return populationTypeCoding.code;
    }
    return null;
}
exports.codeableConceptToPopulationType = codeableConceptToPopulationType;
/**
 * Pulls the measurement period out of the Measure resource in the provided bundle, assuming one is set.
 * NOTE: the default start/end values are also set in Execution.ts
 * so if this date is changed from 2019 it must also be changed there
 *
 * @param {fhir4.Bundle} measureBundle the FHIR Bundle object containing the Measure resource.
 * @returns {CalculationOptions} object with only the measurement period start/end fields filled out,
 * or the year 2019 set as the calculation period if not set in the Measure.
 */
function extractMeasurementPeriod(measureBundle) {
    var _a, _b, _c;
    var measureEntry = (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.find(function (e) { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'Measure'; });
    if (!measureEntry || !measureEntry.resource) {
        throw new CustomErrors_1.UnexpectedResource('Measure resource was not found in provided measure bundle');
    }
    var measure = measureEntry.resource;
    return {
        measurementPeriodStart: ((_b = measure.effectivePeriod) === null || _b === void 0 ? void 0 : _b.start) || '2019-01-01',
        measurementPeriodEnd: ((_c = measure.effectivePeriod) === null || _c === void 0 ? void 0 : _c.end) || '2019-12-31'
    };
}
exports.extractMeasurementPeriod = extractMeasurementPeriod;
/**
 * Figure out if a string is a  valid URL  or if it's just a string
 * @public
 * @param {string} libraryName - The name of the library depending on the origin of the measure this may a url
 * @return {boolean} If the statement is a function or not.
 */
function isValidLibraryURL(libraryName) {
    var urlFormat = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
    var r = new RegExp(urlFormat);
    return r.test(libraryName);
}
exports.isValidLibraryURL = isValidLibraryURL;
function extractLibrariesFromBundle(measureBundle) {
    var _a;
    var measure = extractMeasureFromBundle(measureBundle);
    var rootLibRef = measure.library[0];
    var rootLibId;
    if (isValidLibraryURL(rootLibRef))
        rootLibId = rootLibRef;
    else
        rootLibId = rootLibRef.substring(rootLibRef.indexOf('/') + 1);
    var libraries = [];
    var elmJSONs = [];
    var cqls = [];
    var rootLibIdentifier = {
        id: '',
        version: ''
    };
    (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.forEach(function (e) {
        var _a, _b, _c;
        if (((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) == 'Library') {
            var library_1 = e.resource;
            libraries.push(library_1);
            var elmsEncoded = (_b = library_1.content) === null || _b === void 0 ? void 0 : _b.filter(function (a) { return a.contentType === 'application/elm+json'; });
            elmsEncoded === null || elmsEncoded === void 0 ? void 0 : elmsEncoded.forEach(function (elmEncoded) {
                var _a, _b;
                if (elmEncoded.data) {
                    var decoded = Buffer.from(elmEncoded.data, 'base64').toString('binary');
                    var elm = JSON.parse(decoded);
                    if (library_1.url == rootLibId) {
                        rootLibIdentifier = elm.library.identifier;
                    }
                    else if (library_1.id === rootLibId) {
                        rootLibIdentifier = elm.library.identifier;
                    }
                    if ((_b = (_a = elm.library) === null || _a === void 0 ? void 0 : _a.includes) === null || _b === void 0 ? void 0 : _b.def) {
                        elm.library.includes.def = elm.library.includes.def.map(function (def) {
                            def.path = def.path.substring(def.path.lastIndexOf('/') + 1);
                            return def;
                        });
                    }
                    elmJSONs.push(elm);
                }
            });
            var cqlsEncoded = (_c = library_1.content) === null || _c === void 0 ? void 0 : _c.filter(function (a) { return a.contentType === 'text/cql'; });
            cqlsEncoded === null || cqlsEncoded === void 0 ? void 0 : cqlsEncoded.forEach(function (elmEncoded) {
                if (elmEncoded.data) {
                    var decoded = Buffer.from(elmEncoded.data, 'base64').toString('binary');
                    var cql = {
                        name: library_1.name || library_1.id || 'unknown library',
                        cql: decoded
                    };
                    cqls.push(cql);
                }
            });
        }
    });
    if (rootLibIdentifier.id === '') {
        throw new CustomErrors_1.UnexpectedResource('No Root Library could be identified in provided measure bundle');
    }
    return { cqls: cqls, rootLibIdentifier: rootLibIdentifier, elmJSONs: elmJSONs };
}
exports.extractLibrariesFromBundle = extractLibrariesFromBundle;
function extractMeasureFromBundle(measureBundle) {
    var _a;
    var measureEntry = (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.find(function (e) { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'Measure'; });
    if (!measureEntry) {
        throw new CustomErrors_1.UnexpectedResource('Measure resource does not exist in provided measure bundle');
    }
    var measure = measureEntry.resource;
    if (!measure.library) {
        throw new CustomErrors_1.UnexpectedProperty('Measure resource must specify a "library"');
    }
    return measure;
}
exports.extractMeasureFromBundle = extractMeasureFromBundle;
//# sourceMappingURL=MeasureBundleHelpers.js.map